import { createServerFn } from "@tanstack/react-start";
import { z } from "zod";
import { createClient } from "@supabase/supabase-js";
import type { Database } from "@/integrations/supabase/types";

/**
 * Public prize-bond checker. Reads via a publishable-key client that
 * inherits the public `TO anon` SELECT policy. Never uses the admin
 * client. Bulk/range are capped so a single call cannot fan out into a
 * scan of millions of rows.
 */

const BULK_MAX = 100;
const RANGE_MAX = 500;
const RL_WINDOW_MS = 60_000;
const RL_MAX = 30;

// Per-isolate coarse rate-limit bucket. Not per-IP (headers vary across
// runtimes), but prevents runaway hot loops from a single worker.
let RL_WINDOW_START = Date.now();
let RL_COUNT = 0;
function rateLimit(): boolean {
  const now = Date.now();
  if (now - RL_WINDOW_START > RL_WINDOW_MS) {
    RL_WINDOW_START = now;
    RL_COUNT = 0;
  }
  RL_COUNT += 1;
  return RL_COUNT <= RL_MAX * 10; // 300/min per isolate
}

function normalizeBondNumbers(list: string[]): string[] {
  const out = new Set<string>();
  for (const raw of list) {
    const s = String(raw ?? "").replace(/\D+/g, "");
    if (s.length >= 3 && s.length <= 8) out.add(s.replace(/^0+/, "") || "0");
  }
  return Array.from(out);
}

function expandRange(from: string, to: string): string[] {
  const a = parseInt(String(from).replace(/\D+/g, ""), 10);
  const b = parseInt(String(to).replace(/\D+/g, ""), 10);
  if (!Number.isFinite(a) || !Number.isFinite(b)) return [];
  const lo = Math.min(a, b);
  const hi = Math.max(a, b);
  if (hi - lo + 1 > RANGE_MAX) return [];
  const out: string[] = [];
  for (let i = lo; i <= hi; i++) out.push(String(i));
  return out;
}

const InputSchema = z.object({
  denomination_id: z.number().int().positive(),
  mode: z.enum(["single", "bulk", "range"]),
  numbers: z.array(z.string()).max(BULK_MAX).optional(),
  range_from: z.string().optional(),
  range_to: z.string().optional(),
});

export type PrizeBondResult = {
  bond_number: string;
  won: boolean;
  prize_category?: number;
  prize_amount?: number;
  draw_number?: number;
  draw_date?: string;
  draw_city?: string;
};

export const checkPrizeBond = createServerFn({ method: "POST" })
  .inputValidator((input: unknown) => InputSchema.parse(input))
  .handler(async ({ data }) => {
    if (!rateLimit()) {
      return { ok: false as const, error: "rate_limit", results: [] as PrizeBondResult[] };
    }

    let queryNumbers: string[] = [];
    if (data.mode === "single" || data.mode === "bulk") {
      queryNumbers = normalizeBondNumbers(data.numbers ?? []);
    } else if (data.mode === "range") {
      queryNumbers = expandRange(data.range_from ?? "", data.range_to ?? "");
    }
    if (queryNumbers.length === 0) {
      return { ok: false as const, error: "no_valid_numbers", results: [] as PrizeBondResult[] };
    }
    if (queryNumbers.length > RANGE_MAX) queryNumbers = queryNumbers.slice(0, RANGE_MAX);

    const supa = createClient<Database>(
      process.env.SUPABASE_URL!,
      process.env.SUPABASE_PUBLISHABLE_KEY!,
      { auth: { storage: undefined, persistSession: false, autoRefreshToken: false } },
    );

    const { data: draws, error: drawsErr } = await supa
      .from("prize_bond_draws")
      .select("id, draw_number, draw_date, draw_city, first_prize_amount, second_prize_amount, third_prize_amount")
      .eq("denomination_id", data.denomination_id);
    if (drawsErr) {
      return { ok: false as const, error: "db_draws", results: [] as PrizeBondResult[] };
    }
    const drawIds = (draws ?? []).map((d) => d.id);
    const drawById = new Map((draws ?? []).map((d) => [d.id, d]));

    let winners: Array<{ bond_number: string; prize_category: number; draw_id: number }> = [];
    if (drawIds.length > 0) {
      const { data: wRows, error: wErr } = await supa
        .from("prize_bond_winners")
        .select("bond_number, prize_category, draw_id")
        .in("draw_id", drawIds)
        .in("bond_number", queryNumbers);
      if (wErr) {
        return { ok: false as const, error: "db_winners", results: [] as PrizeBondResult[] };
      }
      winners = (wRows ?? []) as typeof winners;
    }

    const winnersByNumber = new Map<string, typeof winners[number]>();
    for (const w of winners) {
      const prev = winnersByNumber.get(w.bond_number);
      if (!prev || w.prize_category < prev.prize_category) winnersByNumber.set(w.bond_number, w);
    }

    const results: PrizeBondResult[] = queryNumbers.map((num) => {
      const w = winnersByNumber.get(num);
      if (!w) return { bond_number: num, won: false };
      const d = drawById.get(w.draw_id);
      const amount =
        w.prize_category === 1
          ? d?.first_prize_amount
          : w.prize_category === 2
          ? d?.second_prize_amount
          : d?.third_prize_amount;
      return {
        bond_number: num,
        won: true,
        prize_category: w.prize_category,
        prize_amount: amount ? Number(amount) : undefined,
        draw_number: d?.draw_number,
        draw_date: d?.draw_date ?? undefined,
        draw_city: d?.draw_city ?? undefined,
      };
    });

    return { ok: true as const, results };
  });

export const getLatestPrizeBondDraws = createServerFn({ method: "GET" }).handler(async () => {
  const supa = createClient<Database>(
    process.env.SUPABASE_URL!,
    process.env.SUPABASE_PUBLISHABLE_KEY!,
    { auth: { storage: undefined, persistSession: false, autoRefreshToken: false } },
  );
  const { data, error } = await supa
    .from("prize_bond_draws")
    .select("denomination_id, draw_number, draw_date, draw_city, first_prize_amount")
    .order("draw_date", { ascending: false })
    .limit(20);
  if (error) return { ok: false as const, items: [] };
  return { ok: true as const, items: data ?? [] };
});